import React, { useState } from 'react';
import { Bot, X, Send, Loader2, Lightbulb } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteFormAIAssistant = ({ onServiceRecommendation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`quote-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/ai/chat/quote-assistant`, {
        message: input,
        session_id: sessionId
      });

      const aiMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, aiMessage]);

      // Check if AI recommended a service (simple keyword detection)
      const recommendation = detectServiceRecommendation(response.data.response);
      if (recommendation && onServiceRecommendation) {
        onServiceRecommendation(recommendation);
      }
    } catch (error) {
      console.error('Quote Assistant error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I couldn't process that. Please describe your cleaning needs or use the quote calculator above."
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const detectServiceRecommendation = (text) => {
    const lower = text.toLowerCase();
    if (lower.includes('end of tenancy') || lower.includes('moving out')) return 'End of Tenancy Cleaning';
    if (lower.includes('airbnb') || lower.includes('short-term rental')) return 'Airbnb Cleaning';
    if (lower.includes('deep clean') || lower.includes('spring clean')) return 'Deep Cleaning';
    if (lower.includes('regular') || lower.includes('weekly') || lower.includes('fortnightly')) return 'Regular Cleaning';
    if (lower.includes('commercial') || lower.includes('office')) return 'Commercial Cleaning';
    if (lower.includes('oven')) return 'Oven Cleaning';
    return null;
  };

  const startConversation = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm your quote assistant. Tell me about your cleaning needs, and I'll help you choose the perfect service. What type of property are you looking to clean?",
        id: 'welcome-quote'
      }]);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={startConversation}
        className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-3 rounded-lg shadow-lg transition-all duration-300 hover:scale-105"
      >
        <Bot className="w-5 h-5" />
        <span className="font-semibold">Need Help Choosing?</span>
        <Lightbulb className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-2xl border-2 border-purple-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold">Quote Assistant</h3>
            <p className="text-xs text-purple-100">Let me help you choose</p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="hover:bg-white/20 p-2 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="h-80 overflow-y-auto p-4 space-y-3 bg-slate-50">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] px-4 py-2 rounded-lg text-sm ${
                msg.role === 'user'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-slate-800 shadow'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white shadow px-4 py-2 rounded-lg flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
              <span className="text-sm text-slate-600">Analyzing...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-slate-200 bg-white">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Describe your cleaning needs..."
            disabled={loading}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 disabled:bg-slate-100 text-sm"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteFormAIAssistant;
